<template>
  <div class="cs-image" :class="`is-${status}`" :style="{width: this.width, height: this.height}">
    <div class="cs-image__placeholder" v-if="status !== 'loaded'">
      <v-icon v-if="status === 'error'" class="cs-image__error-icon">broken_image</v-icon>
    </div>
    <img :alt="alt" :src="src" :style="imageStyles" :loading="loadingAttr" :fetchpriority="fetchPriorityAttr"
         decoding="async" @load="onLoad" @error="onError" ref="img"/>
  </div>
</template>

<style lang="stylus" scoped>
  .cs-image
    position relative
    overflow hidden

  img
    display block
    object-fit contain
    object-position center center
    opacity 0
    transition opacity 180ms ease-in

  .cs-image.is-loaded img
    opacity 1

  .cs-image__placeholder
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    overflow hidden
    display flex
    align-items center
    justify-content center
    background-color var(--skeleton-base, #e9e9ec)

  .cs-image.is-loading .cs-image__placeholder::after
    content ''
    position absolute
    top 0
    left 0
    width 100%
    height 100%
    background linear-gradient(90deg, transparent 0%, var(--skeleton-sheen, #f4f4f6) 50%, transparent 100%)
    animation cs-image-shimmer 1.5s ease-in-out infinite

  .cs-image__error-icon
    color var(--skeleton-icon, #9a9aa0)
    opacity 0.7

  @keyframes cs-image-shimmer
    0%
      transform translateX(-100%)
    100%
      transform translateX(100%)

  @media (prefers-reduced-motion: reduce)
    img
      transition none
    .cs-image.is-loading .cs-image__placeholder::after
      animation none
</style>

<script lang="ts">
  import { Component, Prop, Watch } from 'vue-property-decorator'
  import { CSVue } from '@/cs/CSVue'

  @Component
  export default class CSImage extends CSVue {
    @Prop({ required: true }) src!: string
    @Prop({ required: true }) width!: string
    @Prop({ required: true }) height!: string
    @Prop({ default: '' }) alt!: string
    @Prop({ default: false }) cover!: boolean
    @Prop({ default: 'center center' }) position!: string
    @Prop({ default: false }) eager!: boolean

    status: 'loading' | 'loaded' | 'error' = 'loading'

    get imageStyles() {
      return {
        'width': '100%',
        'height': '100%',
        'object-fit': this.cover ? 'cover' : 'contain',
        'object-position': this.position
      }
    }

    get loadingAttr() {
      return this.eager ? 'eager' : 'lazy'
    }

    get fetchPriorityAttr() {
      return this.eager ? 'high' : 'auto'
    }

    private syncStatusFromImg() {
      const img = this.$refs.img as HTMLImageElement
      if (img && img.complete) this.status = img.naturalWidth > 0 ? 'loaded' : 'error'
    }

    mounted() {
      super.mounted()
      this.syncStatusFromImg()
    }

    @Watch('src')
    onSrcChanged() {
      this.status = 'loading'
      this.$nextTick(() => this.syncStatusFromImg())
    }

    onLoad() {
      this.status = 'loaded'
    }

    onError() {
      this.status = 'error'
    }
  }
</script>
